// crm-for-retention.js - Tier 3 Solution Page
// Medicare CRM Built for Retention

export const content = {
  heroSubtitle: "Most CRMs help you sell. Pro IRP helps you keep what you've sold. A Medicare CRM designed around the thing that actually matters: stopping churn.",

  problemSection: {
    title: "The Problem With Most Medicare CRMs",
    html: `
      <p>I've used a lot of CRMs over the years. MedicarePRO, AgencyBloc, ProducerMAX, generic solutions like Salesforce—you name it, I've probably tried it. And here's what I've learned: <strong>they're all built for sales, not retention.</strong></p>

      <p>Think about it. Every Medicare CRM on the market is obsessed with lead management, enrollment workflows, and commission tracking. Those things matter, sure. But what happens after the sale? What happens when you need to keep that client from walking away during the next AEP?</p>

      <p>The typical Medicare CRM answer: a reminder to "follow up" at some arbitrary interval. Maybe a birthday notification. That's it. No risk scoring. No churn prediction. No intelligent alerts when something's about to go wrong.</p>

      <p>Meanwhile, research shows that about 21% of Medicare Advantage enrollees switch coverage within a two-year period. One in five clients, gone. And for new enrollees, the numbers are even worse—nearly half change insurance within five years of enrollment.</p>

      <p>That's not a lead problem. That's a retention problem. And most CRMs are completely blind to it.</p>
    `
  },

  features: [
    {
      icon: "target",
      title: "Risk Scoring That Actually Works",
      description: "Every client gets a churn risk score based on real factors: contact recency, plan changes, claim issues, satisfaction signals. Know who's at risk before they start shopping."
    },
    {
      icon: "bell",
      title: "Intelligent Alerts",
      description: "Get notified when a client's plan changes benefits, when their Star rating drops, when formulary changes affect their prescriptions. Catch problems before clients do."
    },
    {
      icon: "clock",
      title: "Automated Retention Workflows",
      description: "The right touchpoint at the right time, triggered automatically. Birthday calls, annual reviews, post-enrollment check-ins, ANOC follow-ups—all systematized."
    },
    {
      icon: "chart",
      title: "Retention Analytics",
      description: "See your actual retention rates, not just enrollment counts. Track churn by agent, by plan, by time period. Understand what's working and what isn't."
    },
    {
      icon: "users",
      title: "Client Health Dashboard",
      description: "Visual overview of your entire book's health. Green, yellow, red indicators showing who needs attention now and who's stable. Prioritize your time effectively."
    },
    {
      icon: "zap",
      title: "One-Click Outreach",
      description: "Call, text, or email directly from client profiles. Log interactions automatically. Pre-built templates for every retention scenario."
    }
  ],

  featuresTitle: "A CRM Built Around Keeping Clients",

  mainContent: `
    <h2>Why Retention-First Changes Everything</h2>

    <p>Let me put this in perspective. The average Medicare agent pays somewhere between $30-100 per lead. That's just to get someone on the phone. Then you've got the time to quote, explain plans, handle objections, complete enrollment. All told, acquiring a new client might cost you $150-300 in real costs.</p>

    <p>Now, a retained client? They cost almost nothing to keep. A few phone calls a year, some proactive outreach, maybe 30 minutes of your time total. And they keep paying you commissions year after year.</p>

    <p>The math is obvious: retention is the highest-ROI activity in this business. So why is your CRM optimized for the low-ROI activity instead?</p>

    <h2>What Makes Pro IRP Different</h2>

    <p>Pro IRP was built by someone who actually sells Medicare (that's me). Not by a software company that read some articles about the industry. I've lived the frustration of watching clients walk away for preventable reasons, and I built the tool I wish I'd had.</p>

    <h3>It Thinks in Retention Terms</h3>

    <p>Every feature, every screen, every report is oriented around one question: how do I keep more clients? The dashboard doesn't show you "leads in pipeline"—it shows you client risk scores, upcoming renewal dates, and who needs a call today.</p>

    <h3>It's Proactive, Not Reactive</h3>

    <p>Most CRMs wait for you to do something. Pro IRP tells you what needs to be done. "This client's plan is losing their doctor next month." "This client hasn't been contacted in 90 days." "This client is in a plan with a declining Star rating." You're not guessing who needs attention—the system tells you.</p>

    <h3>It's Built for Medicare Specifically</h3>

    <p>Not insurance generally. Medicare specifically. That means built-in understanding of AEP, OEP, SEP rules. Understanding of MA vs Med Supp vs PDP dynamics. Understanding of Star ratings, ANOC letters, formulary changes. The things that actually drive Medicare churn.</p>

    <h2>Who This Is For</h2>

    <p>Pro IRP is for agents and agencies who:</p>

    <ul>
      <li>Have a book of business they want to protect (typically 100+ clients)</li>
      <li>Are tired of losing clients to competitors during AEP</li>
      <li>Want systematic retention processes, not ad-hoc "I should call them sometime"</li>
      <li>Understand that keeping clients is more profitable than constantly replacing them</li>
    </ul>

    <p>If you're a brand new agent with no book, you probably don't need this yet—focus on building first. But if you've got clients to protect, this is built for you.</p>

    <h2>The Technology</h2>

    <p>Pro IRP uses machine learning to calculate risk scores based on dozens of factors. It integrates with plan data to alert you to changes that affect your clients. It automates outreach workflows while keeping everything compliant with CMS regulations.</p>

    <p>But honestly? The technology isn't the point. The point is that you'll keep more clients, make more money, and spend less time on the hamster wheel of constant client replacement.</p>

    <p>That's what a retention-first CRM actually delivers.</p>
  `,

  faqs: [
    {
      question: "How is Pro IRP different from other Medicare CRMs?",
      answer: "Most Medicare CRMs focus on sales and enrollment workflows. Pro IRP is built specifically for retention—featuring risk scoring, churn prediction, automated retention workflows, and proactive alerts about plan changes that affect your clients. It's designed to help you keep clients, not just acquire them."
    },
    {
      question: "Do I need to replace my existing CRM?",
      answer: "Pro IRP is designed to work as your primary client management system, but it can complement existing tools if needed. Many agents use Pro IRP for retention management while keeping their carrier-provided enrollment tools for initial sales."
    },
    {
      question: "How does the risk scoring work?",
      answer: "Pro IRP analyzes multiple factors including contact recency, plan change history, claim issues, satisfaction indicators, and plan-level changes (like Star rating drops or benefit modifications). Each client gets a score from 0-100 indicating their churn risk, so you know exactly who needs attention."
    },
    {
      question: "Is Pro IRP compliant with CMS marketing regulations?",
      answer: "Yes. Pro IRP is built with CMS compliance in mind. Automated communications follow CMS guidelines, and the system helps you document scope of appointment and maintain compliant contact records."
    },
    {
      question: "What size book of business is Pro IRP designed for?",
      answer: "Pro IRP is most valuable for agents with 100+ clients where manual tracking becomes difficult. However, the systematic approach benefits agencies of any size. Solo agents, small agencies, and large FMOs all use Pro IRP—just at different scales."
    },
    {
      question: "How long does it take to set up?",
      answer: "Most agents are up and running within a day. You can import your existing client list via CSV, and the system begins calculating risk scores immediately. Automated workflows can be configured in minutes using pre-built templates."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage in 2024: Enrollment Update and Key Trends",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "Kaiser Family Foundation",
      accessedDate: "2024-12-10"
    },
    {
      title: "Turnover Among New Medicare Advantage Enrollees May Be Greater Than Perceived",
      url: "https://www.ajmc.com/view/turnover-among-new-medicare-advantage-enrollees-may-be-greater-than-perceived",
      publisher: "AJMC",
      accessedDate: "2024-12-10"
    },
    {
      title: "Best Medicare CRM Solutions Guide",
      url: "https://marutitech.com/best-medicare-crm-solutions-guide/",
      publisher: "MarutiTech",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
